import { hueFor, initials } from './avatar';
import { decodeHtmlEntities } from '../../lib/decode-entities';
import type { EmailCardData } from './types';

/**
 * Presentational email result card. Mirrors the visual language of
 * OnboardingRevealCard (bordered card chrome, 34px circular avatar, muted meta).
 * The whole card is clickable → onOpen().
 */

/** e.g. "Thu, 3:12 PM". Falls back to the raw string if unparseable. */
function formatTime(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    weekday: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function EmailResultCard({ data, onOpen }: { data: EmailCardData; onOpen: () => void }) {
  const { fromName, fromEmail, subject, snippet, date, isUnread, matchedIndex, matchedOf, senderPhotoUrl } = data;

  const matchLabel =
    matchedIndex != null
      ? matchedOf != null
        ? `MATCHED ${matchedIndex} OF ${matchedOf}`
        : `MATCHED ${matchedIndex}`
      : null;

  const displayName = fromName || fromEmail || 'Unknown sender';

  return (
    <button type="button" data-testid="email-result-card" data-message-id={data.messageId} className="rc-card rc-email" onClick={onOpen}>
      <div className="rc-meta">
        <span className="rc-meta-kind">📧 EMAIL</span>
        {matchLabel && (
          <>
            <span className="rc-meta-sep">·</span>
            <span className="rc-meta-match">{matchLabel}</span>
          </>
        )}
        {isUnread && (
          <>
            <span className="rc-meta-sep">·</span>
            <span className="rc-meta-unread">UNREAD</span>
          </>
        )}
      </div>

      <div className="rc-row">
        {senderPhotoUrl ? (
          <img className="rc-av" src={senderPhotoUrl} alt="" />
        ) : (
          <div className="rc-av rc-av-initials" style={{ background: hueFor(fromEmail || fromName || '?') }}>
            {initials(fromName, fromEmail)}
          </div>
        )}
        <span className="rc-sender">{displayName}</span>
        <span className="rc-time">{formatTime(date)}</span>
      </div>

      <div className="rc-subject">{decodeHtmlEntities(subject)}</div>
      {snippet && <div className="rc-snippet">{decodeHtmlEntities(snippet)}</div>}
    </button>
  );
}
